import { Stack } from "@mui/material";
import {
  CbhIcon,
  type CbhIconProps,
  Divider,
  Text,
} from "@src/appV2/redesign/system/ui-components";

import { IosSwitch } from "../../components/IosSwitch";

export interface NotificationPreferenceCapability {
  type: "push" | "sms";
  enabled: boolean;
}

export interface NotificationPreferenceItemProps {
  preferenceKey: string;
  iconType: CbhIconProps["type"];
  title: string;
  description: string;
  capabilities: NotificationPreferenceCapability[];
  bottomDivider?: boolean;
  disabled?: boolean;
  onUpdate: (preferenceKey: string, capability: NotificationPreferenceCapability) => void;
}

export function NotificationPreferenceItem(props: NotificationPreferenceItemProps) {
  const {
    preferenceKey,
    iconType,
    title,
    description,
    capabilities,
    bottomDivider = true,
    disabled = false,
    onUpdate,
  } = props;

  return (
    <Stack
      data-testid={`notification-preference-${preferenceKey}`}
      sx={(theme) => ({ width: "100%", paddingRight: theme.spacing(2) })}
    >
      <Stack direction="row" spacing={5} sx={{ width: "100%" }}>
        <CbhIcon type={iconType} size="medium" />
        <Stack spacing={6} sx={{ width: "100%" }}>
          <Stack spacing={1}>
            <Text semibold variant="h6">
              {title}
            </Text>
            <Text
              semibold
              variant="body2"
              sx={(theme) => ({ color: theme.palette.text.secondary })}
            >
              {description}
            </Text>
          </Stack>
          <Stack spacing={4}>
            {capabilities.map((capability) => (
              <Stack
                key={capability.type}
                data-testid={`notification-preference-${preferenceKey}-${capability.type}`}
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
              >
                <Text semibold variant="body1">
                  {capability.type === "push" ? "Push notifications" : "SMS notifications"}
                </Text>
                <IosSwitch
                  checked={capability.enabled}
                  onChange={() => {
                    if (disabled) {
                      return;
                    }

                    onUpdate(preferenceKey, { ...capability, enabled: !capability.enabled });
                  }}
                />
              </Stack>
            ))}
          </Stack>
          {bottomDivider && <Divider />}
        </Stack>
      </Stack>
    </Stack>
  );
}
